import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Robot, EnvelopeSimple, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import { supabase, supabaseConfigured } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState("magic"); // "magic" | "password"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center text-muted-foreground">
        Lade…
      </div>
    );
  }
  if (user) {
    const redirect = location.state?.from?.pathname || "/";
    return <Navigate to={redirect} replace />;
  }

  const handleMagic = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      setMagicSent(true);
      toast.success("Magic-Link verschickt — check deine Mails.");
    } catch (err) {
      toast.error(err.message || "Login fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) throw error;
      toast.success("Eingeloggt.");
    } catch (err) {
      toast.error(err.message || "Login fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  if (!supabaseConfigured) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="bg-card border border-border rounded-lg p-8 max-w-md text-center">
          <h1 className="font-display font-semibold text-2xl mb-2">Auth nicht konfiguriert</h1>
          <p className="text-muted-foreground text-sm">
            Setze REACT_APP_SUPABASE_URL und REACT_APP_SUPABASE_ANON_KEY in
            <code className="bg-muted mx-1 px-1 rounded">frontend/.env</code>, dann Frontend neu
            starten.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary text-white mb-4">
            <Robot size={28} weight="duotone" />
          </div>
          <h1 className="font-display font-semibold text-2xl">Auto-Reply Bot</h1>
          <p className="text-muted-foreground text-sm mt-1">Düren Badesee</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          {magicSent ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center mb-3">
                <EnvelopeSimple size={24} weight="duotone" />
              </div>
              <div className="font-display font-semibold text-lg">Check deine Mails</div>
              <p className="text-sm text-muted-foreground mt-2">
                Wir haben einen Login-Link an <b>{email}</b> geschickt. Klick drauf, dann bist
                du eingeloggt.
              </p>
              <button
                onClick={() => setMagicSent(false)}
                className="mt-4 text-xs text-primary hover:underline"
              >
                Andere Email verwenden
              </button>
            </div>
          ) : (
            <>
              <div className="flex gap-2 bg-secondary rounded-md p-1 mb-4">
                <button
                  onClick={() => setMode("magic")}
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    mode === "magic"
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground"
                  }`}
                >
                  Magic Link
                </button>
                <button
                  onClick={() => setMode("password")}
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    mode === "password"
                      ? "bg-white text-foreground shadow-sm"
                      : "text-muted-foreground"
                  }`}
                >
                  Passwort
                </button>
              </div>

              {mode === "magic" ? (
                <form onSubmit={handleMagic} className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground font-medium mb-1.5 block">
                      Email
                    </label>
                    <div className="relative">
                      <EnvelopeSimple
                        size={16}
                        className="absolute left-3 top-3 text-muted-foreground"
                      />
                      <input
                        type="email"
                        autoFocus
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="du@beispiel.de"
                        className="w-full border border-border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || !email.trim()}
                    className="w-full bg-primary text-white rounded-md py-2.5 font-medium hover:bg-primary/90 disabled:opacity-40"
                  >
                    {submitting ? "Sende…" : "Login-Link per Mail schicken"}
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    Du bekommst eine Mail mit Login-Link. Kein Passwort nötig.
                  </p>
                </form>
              ) : (
                <form onSubmit={handlePassword} className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground font-medium mb-1.5 block">
                      Email
                    </label>
                    <div className="relative">
                      <EnvelopeSimple
                        size={16}
                        className="absolute left-3 top-3 text-muted-foreground"
                      />
                      <input
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="du@beispiel.de"
                        className="w-full border border-border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground font-medium mb-1.5 block">
                      Passwort
                    </label>
                    <div className="relative">
                      <LockKey
                        size={16}
                        className="absolute left-3 top-3 text-muted-foreground"
                      />
                      <input
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full border border-border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || !email.trim() || !password}
                    className="w-full bg-primary text-white rounded-md py-2.5 font-medium hover:bg-primary/90 disabled:opacity-40"
                  >
                    {submitting ? "Logge ein…" : "Einloggen"}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Neue User legt der Admin direkt im Supabase-Dashboard an.
        </p>
      </div>
    </div>
  );
}
